import { Injectable } from '@angular/core';

export interface ShadowPreset {
  id: string;
  name: string;
  type: 'programmatic';
  config?: ProgrammaticShadowConfig;
}

export interface ProgrammaticShadowConfig {
  type: 'drop' | 'perspective' | 'reflection' | 'glow';
  color: string;
  blur: number;
  offsetX: number;
  offsetY: number;
  opacity: number;
  scale?: number;
}

export const SHADOW_PRESETS: ShadowPreset[] = [
  { id: 'none', name: 'No Shadow', type: 'programmatic' },
  { id: 'drop-soft', name: 'Soft Drop', type: 'programmatic', config: { type: 'drop', color: '#000000', blur: 20, offsetX: 0, offsetY: 10, opacity: 30 } },
  { id: 'drop-hard', name: 'Hard Drop', type: 'programmatic', config: { type: 'drop', color: '#000000', blur: 5, offsetX: 5, offsetY: 5, opacity: 50 } },
  { id: 'perspective', name: 'Perspective', type: 'programmatic', config: { type: 'perspective', color: '#000000', blur: 15, offsetX: 0, offsetY: 30, opacity: 40, scale: 1.2 } },
  { id: 'reflection', name: 'Reflection', type: 'programmatic', config: { type: 'reflection', color: '#000000', blur: 0, offsetX: 0, offsetY: 0, opacity: 30, scale: 1 } },
  { id: 'glow', name: 'Glow', type: 'programmatic', config: { type: 'glow', color: '#ffffff', blur: 30, offsetX: 0, offsetY: 0, opacity: 50 } },
];

@Injectable({
  providedIn: 'root'
})
export class ShadowEffectsService {

  /**
   * Get available shadow presets
   */
  getPresets(): ShadowPreset[] {
    return SHADOW_PRESETS;
  }

  /**
   * Generate programmatic shadow canvas
   */
  generateProgrammaticShadow(
    type: string,
    subjectImage: HTMLImageElement,
    config?: ProgrammaticShadowConfig
  ): HTMLCanvasElement | null {
    if (!subjectImage || type === 'none') return null;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const padding = 100;
    canvas.width = subjectImage.width + padding * 2;
    canvas.height = subjectImage.height + padding * 2;

    switch (type) {
      case 'drop-soft':
        return this.createDropShadow(ctx, canvas, subjectImage, padding, 20, 0, 10, 0.3);
      case 'drop-hard':
        return this.createDropShadow(ctx, canvas, subjectImage, padding, 5, 5, 5, 0.5);
      case 'perspective':
        return this.createPerspectiveShadow(ctx, canvas, subjectImage, padding);
      case 'reflection':
        return this.createReflection(ctx, canvas, subjectImage, padding);
      case 'glow':
        return this.createGlow(ctx, canvas, subjectImage, padding, '#ffffff');
      default:
        if (config) {
          return this.createFromConfig(ctx, canvas, subjectImage, padding, config);
        }
        return null;
    }
  }

  private createDropShadow(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    padding: number,
    blur: number,
    offsetX: number,
    offsetY: number,
    opacity: number
  ): HTMLCanvasElement {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.filter = `blur(${blur}px)`;

    ctx.drawImage(image, padding + offsetX, padding + offsetY);

    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.restore();
    return canvas;
  }

  private createPerspectiveShadow(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    padding: number
  ): HTMLCanvasElement {
    ctx.save();

    ctx.translate(padding, padding + image.height);
    ctx.scale(1.2, 0.3);
    ctx.globalAlpha = 0.4;
    ctx.filter = 'blur(15px)';

    ctx.drawImage(image, -image.width * 0.1, 0);

    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = '#000000';
    ctx.fillRect(-padding * 2, -padding * 2, canvas.width * 2, canvas.height * 2);

    ctx.restore();
    return canvas;
  }

  private createReflection(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    padding: number
  ): HTMLCanvasElement {
    ctx.save();

    ctx.translate(padding, padding + image.height * 2);
    ctx.scale(1, -1);

    ctx.globalAlpha = 0.3;
    ctx.drawImage(image, 0, 0);

    const gradient = ctx.createLinearGradient(0, 0, 0, image.height);
    gradient.addColorStop(0, 'rgba(255,255,255,0)');
    gradient.addColorStop(0.5, 'rgba(255,255,255,1)');
    gradient.addColorStop(1, 'rgba(255,255,255,1)');

    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, image.width, image.height);

    ctx.restore();
    return canvas;
  }

  private createGlow(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    padding: number,
    color: string
  ): HTMLCanvasElement {
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.filter = 'blur(30px)';

    ctx.drawImage(image, padding, padding);

    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.restore();
    return canvas;
  }

  private createFromConfig(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    padding: number,
    config: ProgrammaticShadowConfig
  ): HTMLCanvasElement {
    ctx.save();
    ctx.globalAlpha = config.opacity / 100;
    ctx.filter = `blur(${config.blur}px)`;

    ctx.drawImage(image, padding + config.offsetX, padding + config.offsetY);

    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = config.color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.restore();
    return canvas;
  }
}
